import { useMutation, useQuery } from '@tanstack/react-query';

import { handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { AlertSeverityEnum } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

const BASE_URL = `${urlBackend}api/v1/gestion-tickets/clientes/`;

export interface IImportSoftnetErrorDetalle {
  identificacion?: string | null;
  mensaje: string;
}

export type IImportSoftnetAccion = 'nuevo' | 'actualizado' | 'sin_contratos';

export interface IImportSoftnetClienteMigrado {
  identificacion: string | null;
  nombre: string | null;
  accion: IImportSoftnetAccion;
  contratos: number;
  contratos_nuevos: number;
  equipos: number;
  tickets_historicos: number;
}

export interface IImportSoftnetResponse {
  solicitados: number;
  procesados: number;
  clientes_nuevos: number;
  clientes_actualizados: number;
  contratos_nuevos: number;
  contratos_actualizados: number;
  tickets_historicos_nuevos: number;
  tickets_historicos_actualizados: number;
  clientes_sin_contratos: number;
  errores: number;
  errores_detalle?: (string | IImportSoftnetErrorDetalle)[];
  clientes_migrados?: IImportSoftnetClienteMigrado[];
  duracion_ms: number;
}

export interface IImportSoftnetRangoParams {
  cantidad: number;
  fecha_desde: string;
  fecha_hasta: string;
}

export interface IImportSoftnetCedulasParams {
  identificaciones: string[];
}

export const useImportSoftnetRango = () => {
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async ({ cantidad, fecha_desde, fecha_hasta }: IImportSoftnetRangoParams) => {
      const qs = new URLSearchParams({
        cantidad: String(cantidad),
        fecha_desde,
        fecha_hasta,
      }).toString();
      return post<IImportSoftnetResponse>(`${BASE_URL}import-softnet?${qs}`, {}, true);
    },
    onSuccess: (res) => {
      const data = (res as any)?.data || {};
      const msg =
        `Procesados: ${data.procesados ?? 0} · ` +
        `Clientes nuevos: ${data.clientes_nuevos ?? 0} · ` +
        `Contratos: ${data.contratos_nuevos ?? 0} · ` +
        `Errores: ${data.errores ?? 0}`;
      setAlert?.(true, `Migración Softnet completada. ${msg}`, AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const useImportSoftnetCedulas = () => {
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async ({ identificaciones }: IImportSoftnetCedulasParams) => {
      return post<IImportSoftnetResponse>(
        `${BASE_URL}import-softnet-cedulas`,
        { identificaciones },
        true,
      );
    },
    onSuccess: (res) => {
      const data = (res as any)?.data || {};
      const msg =
        `Solicitados: ${data.solicitados ?? 0} · ` +
        `Procesados: ${data.procesados ?? 0} · ` +
        `Clientes nuevos: ${data.clientes_nuevos ?? 0} · ` +
        `Errores: ${data.errores ?? 0}`;
      setAlert?.(true, `Migración Softnet por cédulas completada. ${msg}`, AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

// =============================================================================
// Sync de datos extendidos Softnet (datos_tecnicos, equipos, materiales,
// pagos, deudas, fibra_points historial, servicios_adicionales) sobre
// clientes YA migrados. Idempotente: solo enriquece datos faltantes, NO
// crea clientes nuevos. Tambien corre automaticamente cada noche.
// =============================================================================

export interface ISyncDatosExtendidosResponse {
  clientes_procesados: number;
  contratos_actualizados: number;
  equipos_creados: number;
  equipos_actualizados: number;
  materiales_creados: number;
  materiales_actualizados: number;
  pagos_creados: number;
  pagos_actualizados: number;
  deudas_creadas: number;
  deudas_actualizadas: number;
  fibra_points_creados: number;
  fibra_points_actualizados: number;
  errores: string[];
  duracion_ms: number;
}

// El backend ahora corre el sync en background (devuelve 202). Polleamos
// el endpoint /estado hasta que en_ejecucion=false, asi el modal del
// frontend sigue mostrando "Sincronizando..." sin que el gateway/nginx
// timeout (504) cuando el proceso tarda minutos. mutationFn solo resuelve
// cuando el job termino y trae el resumen final.
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_DURATION_MS = 30 * 60 * 1000; // 30 min: limite duro de seguridad

interface ISyncEstadoResponse {
  en_ejecucion: boolean;
  iniciado_en: string | null;
  finalizado_en: string | null;
  ultimo_resultado: ISyncDatosExtendidosResponse | null;
  ultimo_error: string | null;
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export const useSyncDatosExtendidos = () => {
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (): Promise<{ data: ISyncDatosExtendidosResponse }> => {
      // 1) Disparar el job. El backend responde 202 (o 409 si ya esta corriendo;
      //    en ese caso simplemente nos sumamos al poll del job actual).
      try {
        await post<ISyncEstadoResponse>(`${BASE_URL}sync-datos-extendidos`, {}, true);
      } catch (err: any) {
        // 409 = ya hay un job en curso. Lo tratamos como "engancharnos" al poll.
        const status = err?.response?.status;
        if (status !== 409) throw err;
      }

      // 2) Pollear el estado hasta que termine.
      const deadline = Date.now() + POLL_MAX_DURATION_MS;
      while (Date.now() < deadline) {
        await sleep(POLL_INTERVAL_MS);
        const estado = await get<ISyncEstadoResponse>(
          `${BASE_URL}sync-datos-extendidos/estado`,
          true,
        );
        const body = (estado as any)?.data as ISyncEstadoResponse | undefined;
        if (!body) continue;
        if (!body.en_ejecucion) {
          if (body.ultimo_error) {
            throw new Error(body.ultimo_error);
          }
          return { data: body.ultimo_resultado ?? ({} as ISyncDatosExtendidosResponse) };
        }
      }
      throw new Error('La sincronizacion excedio el tiempo maximo de espera (30 min)');
    },
    onSuccess: (res) => {
      const data: any = (res as any)?.data || {};
      const equipos = (data.equipos_creados ?? 0) + (data.equipos_actualizados ?? 0);
      const materiales = (data.materiales_creados ?? 0) + (data.materiales_actualizados ?? 0);
      const pagos = (data.pagos_creados ?? 0) + (data.pagos_actualizados ?? 0);
      const deudas = (data.deudas_creadas ?? 0) + (data.deudas_actualizadas ?? 0);
      const fp = (data.fibra_points_creados ?? 0) + (data.fibra_points_actualizados ?? 0);
      const errores = Array.isArray(data.errores) ? data.errores.length : 0;
      const msg =
        `Clientes: ${data.clientes_procesados ?? 0} · ` +
        `Contratos: ${data.contratos_actualizados ?? 0} · ` +
        `Equipos: ${equipos} · Materiales: ${materiales} · ` +
        `Pagos: ${pagos} · Deudas: ${deudas} · Fibra Points: ${fp} · ` +
        `Errores: ${errores}`;
      setAlert?.(true, `Sincronización de datos extendidos completada. ${msg}`,
        AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

// Variante puntual: sincroniza un solo cliente por identificacion. Util
// desde el modal Perfil 360 para forzar el refresh antes de mostrar.
export const useSyncDatosExtendidosUno = () => {
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (identificacion: string) => {
      return post<ISyncDatosExtendidosResponse>(
        `${BASE_URL}sync-datos-extendidos/${encodeURIComponent(identificacion)}`,
        {},
        true,
      );
    },
    onSuccess: (res) => {
      const data: any = (res as any)?.data || {};
      setAlert?.(true,
        `Sincronización del cliente completada (contratos: ${data.contratos_actualizados ?? 0})`,
        AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

// =============================================================================
// Historial de clientes migrados desde Softnet (lectura, no altera el flujo
// de import). Alimenta la pestana "Historial migrados" de la UI.
// =============================================================================

export interface ISoftnetMigratedClient {
  idCliente: string;
  codigo: string;
  identificacion: string | null;
  tipoIdentificacion: string | null;
  razonSocial: string | null;
  ciudad: string | null;
  email: string | null;
  celular: string | null;
  totalContratos: number;
  totalTickets: number;
  totalTicketsHistoricos: number;
  fechaMigracion: string | null;
  fechaUltimaActualizacion: string | null;
}

export interface ISoftnetMigratedHistoryParams {
  page?: number;
  page_size?: number;
  search?: string;
  enabled?: boolean;
}

export const useSoftnetMigratedHistory = ({
  page = 1,
  page_size = 20,
  search = '',
  enabled = true,
}: ISoftnetMigratedHistoryParams = {}) => {
  return useQuery({
    queryKey: ['softnet-migrados', page, page_size, search],
    queryFn: async () => {
      const qs = new URLSearchParams({
        page: String(page),
        page_size: String(page_size),
      });
      if (search) qs.set('search', search);
      return get<ISoftnetMigratedClient[]>(`${BASE_URL}softnet-migrados?${qs.toString()}`, true);
    },
    enabled,
    staleTime: 30_000,
  });
};

// =============================================================================
// Migracion por OLT (tab "Por OLT / ONT")
//
// Paso 1: se le pide al microservicio de provisionamiento que liste las ONT
//         configuradas en una OLT (SNMP walk de solo lectura) y devuelve sus
//         seriales.
// Paso 2: por cada serial se consulta /api/obtenerContratos de Softnet, UNA
//         peticion por serie, y se guarda la respuesta cruda.
//
// Ninguno de los dos pasos modifica la OLT, Softnet ni la base: es una
// consulta de auditoria para decidir despues que campos se migran.
// =============================================================================

/** Paso 1: seriales de ONT leidos de la OLT. */
export interface IMigracionOltPaso1 {
  ip_olt: string;
  us_creacion: string;
  mensaje: string;
  /** El microservicio lo envia como texto, no como numero. */
  total_ONTs: string | null;
  /** Orden de lectura, puede traer duplicados. */
  serial_ont: string[];
  /** La misma lista sin duplicados: es la que se consulta en Softnet. */
  series_unicas: string[];
  duplicados: number;
  duracion_ms: number;
}

export type IMigracionOltEstadoSerie =
  | 'ENCONTRADO'
  | 'SIN_DATOS'
  | 'NO_EXISTE'
  | 'ERROR';

/** Resultado de consultar UNA serie en Softnet. */
export interface IMigracionOltResultadoSerie {
  serie: string;
  estado: IMigracionOltEstadoSerie;
  num_contratos?: number;
  mensaje?: string;
  /** Respuesta cruda de Softnet, tal como llega. */
  response?: unknown;
}

/** Paso 2: resumen del cruce contra Softnet. */
export interface IMigracionOltPaso2 {
  solicitadas: number;
  consultadas: number;
  /** true si habia mas series que el tope configurado en el micro. */
  truncado: boolean;
  max_series_por_ejecucion: number;
  encontrados: number;
  sin_datos: number;
  no_existen: number;
  errores: number;
  duracion_ms: number;
  resultados: IMigracionOltResultadoSerie[];
}

// =============================================================================
// Paso 2.1: Excel de perfiles por OLT (opcional, se carga al inicio).
// =============================================================================

/**
 * Una fila del Excel: el serial y sus perfiles.
 *
 * Los IDs van como TEXTO a proposito: en el archivo real conviven valores
 * numericos (103, 105) con etiquetas ("CLIENTE PYMES", "CLIENTE CORPO").
 */
export interface IMigracionOltPerfilExcel {
  // OJO: ms-tickets serializa con property-naming-strategy SNAKE_CASE
  // (application.yml), así que los campos del record Java llegan en snake_case
  // aunque allá se llamen serialOnt, idLineProfile, etc.
  serial_ont: string;
  service_profile: string | null;
  line_profile: string | null;
  plan: string | null;
  id_line_profile: string | null;
  id_service_profile: string | null;
  nombre: string | null;
  /** Hoja 2 del Excel: upgrade de plan para los planes viejos. */
  plan_actual: string | null;
  plan_migracion: string | null;
  id_plan_migracion: string | null;
}

export interface IMigracionOltExcel {
  archivo: string;
  hoja: string;
  filas_utiles: number;
  sin_serial: number;
  serial_invalido: number;
  duplicados: number;
  sin_service_profile: number;
  service_profiles: string[];
  advertencias: string[];
  perfiles: IMigracionOltPerfilExcel[];
}

/**
 * Carga el Excel de perfiles de la OLT y devuelve el mapa serial -> perfiles.
 *
 * Complementa la validacion del paso 3: los modelos de ONT sin service profile
 * en el inventario del ERP si lo tienen aqui, y el archivo aporta ademas el
 * line profile, el plan y los IDs de ambos perfiles, que son los datos que hace
 * falta tener a mano para aprovisionar.
 *
 * El parseo lo hace el backend (POI): las columnas se localizan por nombre de
 * encabezado, no por posicion, y la validacion de seriales/duplicados vive en
 * un solo lugar.
 */
export const useMigracionOltCargarExcel = () => {
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async ({ archivo, corrida_id }: { archivo: File; corrida_id?: string }) => {
      const form = new FormData();
      form.append('archivo', archivo);
      if (corrida_id) form.append('corrida_id', corrida_id);
      return post<IMigracionOltExcel>(`${BASE_URL}migracion-olt/cargar-excel`, form, true);
    },
    onSuccess: (res) => {
      const d = (res as any)?.data as IMigracionOltExcel | undefined;
      if (!d) return;
      setAlert?.(
        true,
        `Excel leído: ${d.filas_utiles} series con perfiles (hoja "${d.hoja}").`,
        AlertSeverityEnum.success,
      );
    },
    onError: (error) => handleAxiosError(error),
  });
};

/**
 * Paso 3: estado de cada serie frente al catalogo de service profile.
 *   CON_SERVICE_PROFILE - producto resuelto y service profile con valor.
 *   SIN_SERVICE_PROFILE - producto resuelto pero sin service profile.
 *   SIN_PRODUCTO        - la serie no esta en el inventario del ERP.
 *   AMBIGUO             - la serie resuelve a mas de un producto con service
 *                         profiles distintos; no se elige uno, lo revisa una
 *                         persona.
 */
export type IMigracionOltEstadoServiceProfile =
  | 'CON_SERVICE_PROFILE'
  | 'SIN_SERVICE_PROFILE'
  | 'SIN_PRODUCTO'
  | 'AMBIGUO';

/** De dónde salió el service profile que se usará. */
export type IMigracionOltOrigenPerfil = 'ERP' | 'EXCEL' | 'AMBOS' | 'NINGUNO';

export interface IMigracionOltServiceProfileItem {
  serie: string;
  estado: IMigracionOltEstadoServiceProfile;
  /** descripcion del producto en el inventario; en AMBIGUO vienen unidas por " | ". */
  producto: string | null;
  producto_codigo?: string | null;
  /** el perfil resuelto: el del ERP si existe, si no el del Excel. */
  service_profile: string | null;
  origen: IMigracionOltOrigenPerfil;
  /** solo en AMBIGUO: los perfiles candidatos. */
  service_profiles_posibles?: string[];

  // ---- Las dos fuentes, por separado ----
  service_profile_erp: string | null;
  service_profile_excel: string | null;
  /** null si alguna fuente no lo tiene; false = las fuentes se contradicen. */
  erp_excel_coinciden: boolean | null;
  en_excel: boolean;

  // ---- Datos extra del Excel, para el aprovisionamiento que viene ----
  line_profile_excel: string | null;
  plan_excel: string | null;
  id_line_profile: string | null;
  id_service_profile: string | null;
  nombre_excel: string | null;
}

/** Paso 3: resumen de la clasificacion por service profile. */
export interface IMigracionOltPaso3 {
  solicitadas: number;
  con_service_profile: number;
  sin_service_profile: number;
  sin_producto: number;
  ambiguos: number;
  /** cuántas se resolvieron SOLO gracias al Excel (el ERP no las tenía). */
  desde_excel: number;
  /** series donde el ERP y el Excel dicen perfiles distintos. */
  discrepancias: number;
  excel_cargado: boolean;
  /** perfiles distintos encontrados, para saber cuantos hay que configurar. */
  service_profiles: string[];
  duracion_ms: number;
  resultados: IMigracionOltServiceProfileItem[];
}

/**
 * Paso 3: cruza las series que SI tienen contrato contra el inventario del ERP
 * para sacar el producto y su service profile.
 *
 * No va por lotes como el paso 2: es solo lectura sobre base_erp y no llama a
 * ningun sistema externo, asi que una sola peticion alcanza.
 */
export const useMigracionOltServiceProfile = () => {
  return useMutation({
    mutationFn: async ({
      series,
      perfiles_excel,
      corrida_id,
    }: {
      series: string[];
      /** Filas del Excel de la OLT; si van vacías, solo se consulta el ERP. */
      perfiles_excel?: IMigracionOltPerfilExcel[];
      corrida_id?: string;
    }) => {
      return post<IMigracionOltPaso3>(
        `${BASE_URL}migracion-olt/service-profile`,
        { series, perfiles_excel: perfiles_excel ?? [], corrida_id },
        true,
      );
    },
    onError: (error) => handleAxiosError(error),
  });
};

// =============================================================================
// Paso 4: configuracion de la ONT descubierta en la OLT.
// =============================================================================

/** Un service-port de la ONT tal como lo reporta la OLT. */
export interface IMigracionOltServicePort {
  service_port: string | null;
  vlan: string | null;
  gem_port: string | null;
  inbound_traffic_table: string | null;
  outbound_traffic_table: string | null;
}

export interface IMigracionOltConfigItem {
  serie: string;
  estado: 'OK' | 'ERROR';
  /** nombre tal como se envio al micro (sin espacios, con guiones bajos). */
  nombre_cliente_enviado: string;
  ont_id?: string | null;
  puerto_pon?: string | null;
  line_profile?: string | null;
  /** service profile que reporta la OLT. */
  service_profile_olt?: string | null;
  /** service profile que el inventario del ERP tenia previsto (paso 3). */
  service_profile_esperado?: string | null;
  /** false cuando el de la OLT y el del inventario no coinciden. */
  service_profile_coincide?: boolean;
  service_ports?: IMigracionOltServicePort[] | null;
  /** respuesta completa del micro; la usan los pasos siguientes. */
  configuracion?: unknown;
  /**
   * El micro afirma haber borrado incluso en modo consulta (su propia
   * documentacion lo advierte). NO interpretar como eliminacion.
   */
  mensaje_micro_no_confiable?: string;
  mensaje?: string;
}

export interface IMigracionOltPaso4 {
  ip_olt: string;
  solicitadas: number;
  consultadas: number;
  truncado: boolean;
  ok: number;
  errores: number;
  /** cuantas ONT reportan un service profile distinto al del inventario. */
  service_profile_distinto: number;
  /** recordatorio explicito del backend de que no se elimino nada. */
  modo: string;
  duracion_ms: number;
  resultados: IMigracionOltConfigItem[];
}

export interface IMigracionOltConfigEntrada {
  serie: string;
  nombre_cliente: string;
  identificacion: string;
  service_profile_esperado?: string | null;
}

/**
 * Paso 4: descubre en la OLT la configuracion de cada ONT (PON, ONT-ID,
 * perfiles y service-ports con VLAN/GEM/trafico).
 *
 * Es SOLO CONSULTA: el backend llama a cancelarSinValidacion con
 * cancelar_servicios=false, que es su modo de lectura, y ese flag esta fijo del
 * lado del servidor. Desde aqui no hay forma de eliminar una ONT.
 *
 * Es SSH contra la OLT por cada ONT, asi que el componente lo manda en lotes.
 */
export const useMigracionOltConfiguracionOnt = () => {
  return useMutation({
    mutationFn: async ({
      ip_olt,
      items,
    }: {
      ip_olt: string;
      items: IMigracionOltConfigEntrada[];
    }) => {
      return post<IMigracionOltPaso4>(
        `${BASE_URL}migracion-olt/configuracion-ont`,
        { ip_olt, items },
        true,
      );
    },
    onError: (error) => handleAxiosError(error),
  });
};

// =============================================================================
// Paso 5: preparación del aprovisionamiento y migración (destructiva).
// =============================================================================

export type IMigracionOltEstadoPrep =
  | 'LISTO'
  | 'NO_ES_HOME'
  | 'YA_MIGRADO'
  | 'FALTAN_DATOS';

export interface IMigracionOltPrepItem {
  serie: string;
  estado: IMigracionOltEstadoPrep;
  /** Por qué no es HOME o por qué ya está migrado. */
  motivos: string[];
  /** Campos obligatorios que no se pudieron resolver. */
  faltantes: string[];
  /**
   * Campos que no bloquean: salen de la consulta por cliente que se hace justo
   * antes de desconfigurar (puerto_pon, vlan, gem_port).
   */
  se_resuelven_al_migrar?: string[];
  /** De dónde salió la VLAN que se va a enviar. */
  origen_vlan?: string | null;
  nombre_cliente_original: string | null;
  tipo_plan_softnet: string | null;
  /** Columna "LINE PROFILE" del Excel: es el perfil DESTINO, no el de la OLT. */
  line_profile_antiguo: string | null;
  /** Perfil que la OLT reporta hoy. Es el que decide si ya está migrado. */
  line_profile_actual?: string | null;
  /** Hoja 2 del Excel: upgrade de plan. */
  upgrade_de_plan?: boolean;
  plan_actual?: string | null;
  plan_migracion?: string | null;
  id_plan_migracion?: string | null;
  traffic_table_origen?: string;
  aviso_gem_port?: string;
  /** El cuerpo exacto de activarSinValidacion. La clave viaja enmascarada. */
  payload: {
    nombre_cliente: string;
    identificacion: string | null;
    us_creacion: string;
    datos: Record<string, string | null>;
  };
  /** cancelarSinValidacion con cancelar_servicios: false (solo lee). */
  payload_consultar?: Record<string, unknown>;
  /** cancelarSinValidacion con cancelar_servicios: true (desconfigura). */
  payload_desconfigurar?: Record<string, unknown>;
}

/** La OLT según el catálogo del ERP (Configuración de red → OLT). */
export interface IMigracionOltOltErp {
  id_olt?: string;
  nombre?: string;
  /** VLAN del servicio, por OLT. Es la que se envía si la ONT no tiene una. */
  vlan_pppoe?: string | null;
  ip_gestion?: string;
  aviso?: string;
}

export interface IMigracionOltPaso5Prep {
  ip_olt: string;
  nombre_olt: string;
  olt_erp?: IMigracionOltOltErp;
  evaluadas: number;
  listos: number;
  no_es_home: number;
  ya_migrados: number;
  faltan_datos: number;
  catalogo_traffic_table: string[];
  duracion_ms: number;
  resultados: IMigracionOltPrepItem[];
}

/**
 * Previsualiza el aprovisionamiento: arma el payload de cada cliente y lo
 * clasifica. NO ejecuta nada, es solo lectura.
 */
export const useMigracionOltPrepararAprov = () => {
  return useMutation({
    mutationFn: async ({
      ip_olt,
      nombre_olt,
      items,
      corrida_id,
    }: {
      ip_olt: string;
      nombre_olt: string;
      items: Record<string, unknown>[];
      corrida_id?: string;
    }) => {
      return post<IMigracionOltPaso5Prep>(
        `${BASE_URL}migracion-olt/preparar-aprovisionamiento`,
        { ip_olt, nombre_olt, items, corrida_id },
        true,
      );
    },
    onError: (error) => handleAxiosError(error),
  });
};

export type IMigracionOltEstadoFinal =
  | 'RECHAZADO'
  | 'FALLO_CANCELAR'
  | 'FALLO_ACTIVAR'
  | 'COMPLETADO'
  | 'COMPLETADO_AVISO';

/**
 * Una de las tres llamadas al microservicio, con su body y su respuesta.
 * Es lo que la UI muestra como JSON crudo.
 */
export interface IMigracionOltLlamada {
  paso: 'CONSULTAR' | 'CANCELAR' | 'ACTIVAR';
  intento: number;
  ok: boolean;
  /** Endpoint al que se llamó. CONSULTAR y CANCELAR comparten el mismo. */
  api?: string | null;
  metodo?: string | null;
  /** body enviado; la clave PPPoE viaja como "***". */
  request: Record<string, unknown> | null;
  response: Record<string, unknown> | null;
  error: string | null;
  duracion_ms: number;
}

export interface IMigracionOltResultadoMigracion {
  id_ejecucion: string | null;
  /** Corrida (lote) a la que pertenece. */
  corrida_id?: string | null;
  ip_olt?: string | null;
  nombre_olt?: string | null;
  /**
   * La consulta previa dejó guardada la configuración de la ONT. Un rechazado
   * con esto en true NO es un fracaso: la info sirve para aprovisionar después.
   */
  info_capturada?: boolean | null;
  /** Por qué no se migró, en lenguaje de operador. */
  observacion?: string | null;
  reintentos_activacion?: number | null;
  id_contrato_softnet?: string | null;
  /** Cédula/RUC real resuelta. Distinta de lo enviado al micro. */
  cedula?: string | null;
  /** SOFTNET o ERP: de dónde salió la cédula. */
  cedula_origen?: string | null;
  /** Si se pudo escribir en el módulo de aprovisionamiento. */
  aprovisionamiento_ok?: boolean | null;
  aprovisionamiento_mensaje?: string | null;
  aprovisionamiento_contrato?: string | null;
  /** El cliente no estaba en tickets y se importó de Softnet. */
  cliente_creado?: boolean | null;
  serie: string;
  nombre_cliente: string | null;
  estado_final: IMigracionOltEstadoFinal;
  /** Las 3 llamadas en orden: consultar, cancelar, activar. */
  llamadas?: IMigracionOltLlamada[] | null;
  consultar_ok?: boolean | null;
  consultar_mensaje?: string | null;
  /** true = alguien tiene que entrar a la OLT a mano. */
  requiere_revision: boolean;
  motivo_rechazo: string | null;
  cancelar_ok: boolean | null;
  cancelar_intentos: number;
  cancelar_mensaje: string | null;
  activar_ok: boolean | null;
  activar_mensaje: string | null;
  /** Intentos de activación: uno por service profile probado. */
  activar_intentos?: number | null;
  /** Con qué service profile acabó activando y de dónde salió. */
  service_profile_usado?: string | null;
  /** SISTEMA | CATALOGO | GENERICO. */
  service_profile_origen?: string | null;
  ont_id: string | null;
  service_port_id_custom: string | null;
  service_port_id_tr069: string | null;
  potencia: string | null;
  duracion_ms: number;
}

/**
 * DESTRUCTIVO: desconfigura la ONT de UN cliente y la vuelve a aprovisionar.
 *
 * Un cliente por petición: la sesión SSH de la OLT atiende de a uno y la
 * activación es la llamada más lenta. El recorrido de la lista lo hace el
 * componente, que así puede mostrar el avance y pausar.
 *
 * `confirmacion` debe ser igual a `nombre_olt`; el backend lo valida. Es una
 * barrera deliberada: entre el corte y la activación el cliente queda sin
 * servicio.
 *
 * Sin alerta global de éxito/error: el componente pinta el resultado de cada
 * cliente en su bitácora.
 */
export const useMigracionOltMigrarCliente = () => {
  return useMutation({
    mutationFn: async ({
      ip_olt,
      nombre_olt,
      confirmacion,
      item,
      corrida_id,
    }: {
      ip_olt: string;
      nombre_olt: string;
      confirmacion: string;
      item: Record<string, unknown>;
      corrida_id?: string;
    }) => {
      return post<IMigracionOltResultadoMigracion>(
        `${BASE_URL}migracion-olt/migrar-cliente`,
        { ip_olt, nombre_olt, confirmacion, item, corrida_id },
        true,
      );
    },
    onError: (error) => handleAxiosError(error),
  });
};

/**
 * Paso 1 solo: consulta las ONT configuradas en la OLT.
 * El us_creacion lo pone el backend con el usuario autenticado; no se envia
 * desde aqui para que la traza no se pueda falsificar desde el navegador.
 */
export const useMigracionOltConsultarOnt = () => {
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async ({ ip_olt, corrida_id }: { ip_olt: string; corrida_id?: string }) => {
      return post<IMigracionOltPaso1>(
        `${BASE_URL}migracion-olt/consultar-ont`,
        { ip_olt, corrida_id },
        true,
      );
    },
    onSuccess: (res) => {
      const data = (res as any)?.data || {};
      const unicas = data.series_unicas?.length ?? 0;
      setAlert?.(
        true,
        `OLT consultada: ${data.total_ONTs ?? 0} ONT (${unicas} serie(s) única(s)).`,
        unicas > 0 ? AlertSeverityEnum.success : AlertSeverityEnum.warning,
      );
    },
    onError: (error) => handleAxiosError(error),
  });
};

/**
 * Paso 2 solo: consulta Softnet por cada serie recibida.
 *
 * No emite alerta de exito a proposito: la UI llama este hook por lotes (para
 * no exceder el timeout de nginx) y una alerta por lote seria ruido. El
 * componente muestra el progreso y el resumen final. Los errores si se
 * reportan, porque cada lote fallido importa.
 */
export const useMigracionOltConsultarSoftnet = () => {
  return useMutation({
    mutationFn: async ({ series, corrida_id }: { series: string[]; corrida_id?: string }) => {
      return post<IMigracionOltPaso2>(
        `${BASE_URL}migracion-olt/consultar-softnet`,
        { series, corrida_id },
        true,
      );
    },
    onError: (error) => handleAxiosError(error),
  });
};

// Nota: el backend expone ademas POST migracion-olt/consultar, que encadena
// los dos pasos en una sola llamada. La UI NO lo usa a proposito: con muchas
// ONT esa peticion supera el timeout de nginx (60 s) y el navegador recibiria
// 504. El tab llama paso 1 y luego paso 2 por lotes. El endpoint encadenado
// queda para pruebas por curl/Postman con OLT chicas.

// =============================================================================
// Historial de la migracion por OLT
//
// Los pasos de consulta no dejaban rastro: si una corrida salia raro no habia
// forma de volver a ver que contesto Softnet o la OLT en ese momento, y esas
// respuestas cambian con el tiempo. El backend ahora guarda cada consulta con
// su request, su response y lo que tardo.
// =============================================================================

export type IMigracionOltPasoHistorial =
  | 'CONSULTAR_ONT'
  | 'CONSULTAR_SOFTNET'
  | 'SERVICE_PROFILE'
  | 'EXCEL'
  | 'CONFIGURACION_ONT'
  | 'PREPARAR'
  | 'MIGRAR'
  | 'REINTENTO_ACTIVACION';

export interface IMigracionOltHistorialItem {
  id_historial: string;
  ip_olt: string | null;
  nombre_olt: string | null;
  paso: IMigracionOltPasoHistorial;
  /** Texto corto para la lista: la serie, el archivo, "71 serie(s)"... */
  etiqueta: string | null;
  ok: boolean;
  mensaje: string | null;
  /** Lo que tardo la operacion, medido en el micro. */
  duracion_ms: number | null;
  /** Contadores del paso, para ver el resultado sin abrir el detalle. */
  resumen: Record<string, unknown> | null;
  /**
   * Cuánto pesa el detalle. El listado NO lo trae: el de un lote de 20 series
   * de Softnet pesa 1,2 MB, así que 50 filas serían ~60 MB en una respuesta.
   * Se pide por entrada con useMigracionOltHistorialDetalle al abrirla.
   */
  detalle_bytes: number | null;
  /** Corrida (lote) a la que pertenece, para agrupar. */
  corrida_id: string | null;
  us_creacion: string | null;
  fecha_creacion: string;
}

export interface IMigracionOltHistorial {
  ip_olt: string | null;
  limite: number;
  devueltas: number;
  resultados: IMigracionOltHistorialItem[];
}

/**
 * Historial de consultas. `ip_olt` vacio devuelve todas las OLT.
 *
 * Con `staleTime` corto y sin refetch automatico: el historial crece solo
 * cuando el usuario ejecuta un paso, asi que se invalida desde el tab.
 */
export const useMigracionOltHistorial = ({
  ip_olt = '',
  limite = 50,
  enabled = true,
}: { ip_olt?: string; limite?: number; enabled?: boolean } = {}) => {
  return useQuery({
    queryKey: ['migracion-olt-historial', ip_olt, limite],
    queryFn: async () => {
      const qs = new URLSearchParams({ limite: String(limite) });
      if (ip_olt) qs.set('ip_olt', ip_olt);
      return get<IMigracionOltHistorial>(
        `${BASE_URL}migracion-olt/historial?${qs.toString()}`,
        true,
      );
    },
    enabled,
    staleTime: 10_000,
  });
};

/**
 * El request y el response completos de UNA entrada del historial.
 *
 * Aparte del listado porque es grande (1,2 MB para un lote de 20 series de
 * Softnet). Solo se dispara cuando el usuario abre la entrada.
 */
export const useMigracionOltHistorialDetalle = (
  idHistorial: string | null,
  enabled = true,
) => {
  return useQuery({
    queryKey: ['migracion-olt-historial-detalle', idHistorial],
    queryFn: async () =>
      get<{
        id_historial: string;
        paso: string;
        mensaje: string | null;
        duracion_ms: number | null;
        detalle: { request?: unknown; response?: unknown } | null;
      }>(`${BASE_URL}migracion-olt/historial/${idHistorial}`, true),
    enabled: enabled && !!idHistorial,
    // El detalle es inmutable: una vez consultado no cambia nunca.
    staleTime: Infinity,
  });
};

/**
 * Reintenta la activación de un cliente que quedó SIN SERVICIO.
 *
 * Solo el activar: la ONT ya está desconfigurada, así que repetir el cancelar
 * no aporta y puede empeorar el equipo. El resultado se acumula sobre la misma
 * fila de la bitácora, no crea una nueva.
 */
export const useMigracionOltReintentarActivacion = () => {
  return useMutation({
    mutationFn: async ({
      id_ejecucion,
      corrida_id,
    }: {
      id_ejecucion: string;
      corrida_id?: string;
    }) => {
      return post<IMigracionOltResultadoMigracion>(
        `${BASE_URL}migracion-olt/reintentar-activacion`,
        { id_ejecucion, corrida_id },
        true,
      );
    },
    onError: (error) => handleAxiosError(error),
  });
};

/**
 * Ejecuciones que quedaron esperando algo, traídas de la base.
 *
 * La bitácora del paso 4 vive en memoria del navegador: al cerrar la pantalla
 * se pierde, y una corrida de ayer no se podía retomar desde ningún lado. Esto
 * la recupera para poder reintentar la activación.
 *
 * Trae los FALLO_ACTIVAR (los únicos donde el cliente puede estar caído) y los
 * COMPLETADO_AVISO (activaron bien, pero hay que verificar el ACS).
 */
export const useMigracionOltPendientes = ({
  ip_olt = '',
  limite = 50,
  enabled = true,
}: { ip_olt?: string; limite?: number; enabled?: boolean } = {}) => {
  return useQuery({
    queryKey: ['migracion-olt-pendientes', ip_olt, limite],
    queryFn: async () => {
      const qs = new URLSearchParams({ limite: String(limite) });
      if (ip_olt) qs.set('ip_olt', ip_olt);
      return get<{
        ip_olt: string | null;
        devueltas: number;
        sin_servicio: number;
        resultados: IMigracionOltResultadoMigracion[];
      }>(`${BASE_URL}migracion-olt/pendientes?${qs.toString()}`, true);
    },
    enabled,
    staleTime: 10_000,
  });
};

/**
 * El paso 2 —consultar Softnet serie por serie— manejado por el SERVIDOR.
 *
 * Antes era un bucle de esta pantalla en lotes de 20. Recargar a mitad perdía
 * todo lo consultado, y es el paso largo: ~0,3-0,6 s por serie. Ahora se manda
 * la lista una vez y esto solo pregunta cómo va.
 */
export interface IMigracionOltConsultaItem {
  orden: number;
  serie: string;
  /** PENDIENTE | EN_PROCESO | HECHO | OMITIDO */
  estado: string;
  mensaje: string | null;
  fecha_inicio: string | null;
  fecha_fin: string | null;
}

/**
 * Lo que la pantalla necesita para volver al mismo lugar tras una recarga.
 *
 * El Excel importa de verdad: el navegador no puede volver a leer un archivo
 * del disco por su cuenta, así que sin guardarlo acá habría que cargarlo otra
 * vez. El paso 3 y la preparación son rápidos, pero volver a pasar por ellos
 * rompe la ilusión de no haberse ido.
 */
export interface IMigracionOltConsultaContexto {
  paso1?: IMigracionOltPaso1;
  excel?: IMigracionOltExcel | null;
  paso3?: IMigracionOltPaso3;
  prep?: IMigracionOltPaso5Prep;
  nombre_olt?: string;
}

export interface IMigracionOltConsulta {
  id_consulta: string;
  /** La sesión de trabajo que agrupa los pasos en el historial. */
  corrida_id: string | null;
  ip_olt: string;
  nombre_olt: string | null;
  /** EN_CURSO | PAUSADA | TERMINADA | CANCELADA */
  estado: string;
  total: number;
  pendientes: number;
  en_proceso: number;
  hechos: number;
  omitidos: number;
  procesados: number;
  /** La serie que se está consultando en este momento. */
  en_curso_serie: string | null;
  mensaje: string | null;
  us_creacion: string | null;
  fecha_creacion: string | null;
  fecha_fin: string | null;
  contexto: IMigracionOltConsultaContexto | null;
  cola: IMigracionOltConsultaItem[];
  /**
   * Lo consultado, con la misma forma que devolvía el paso 2.
   *
   * `resultados` trae solo el tramo que la pantalla todavía no tiene y ella lo
   * acumula: el resultado de UNA serie pesa 72-160 kB, así que repetir la lista
   * completa en cada sondeo serían megabytes por vuelta.
   */
  paso2: IMigracionOltPaso2 & { desde?: number; desde_siguiente?: number };
  /** Solo en la respuesta de /consulta/activa. */
  hay_consulta?: boolean;
  /** Solo al iniciar: ya había una consulta viva y se devolvió esa. */
  ya_existia?: boolean;
}

export const useMigracionOltConsultaIniciar = () => {
  return useMutation({
    mutationFn: async (body: {
      ip_olt: string;
      nombre_olt?: string | null;
      /** El id con el que se agrupan los pasos en el historial. */
      corrida_id?: string;
      series: string[];
      contexto?: IMigracionOltConsultaContexto;
    }) => post<IMigracionOltConsulta>(`${BASE_URL}migracion-olt/consulta`, body, true),
    onError: (error) => handleAxiosError(error),
  });
};

/**
 * La consulta que la pantalla debe adoptar al cargar, si hay.
 *
 * Devuelve también una ya terminada si es reciente: se puede recargar cuando el
 * paso 2 ya acabó y la tabla tiene que seguir ahí.
 */
export const useMigracionOltConsultaActiva = ({
  ip_olt = '',
  enabled = true,
}: { ip_olt?: string; enabled?: boolean } = {}) => {
  return useQuery({
    queryKey: ['migracion-olt-consulta-activa', ip_olt],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (ip_olt) qs.set('ip_olt', ip_olt);
      return get<IMigracionOltConsulta>(
        `${BASE_URL}migracion-olt/consulta/activa?${qs.toString()}`,
        true,
      );
    },
    enabled,
    staleTime: 0,
  });
};

/**
 * Cómo va una consulta. Mientras está EN_CURSO se repregunta sola. El
 * intervalo es más corto que el de la corrida porque acá cada vuelta son unas
 * 10 series: con 2,5 s la tabla crecería a saltos grandes.
 */
export const useMigracionOltConsultaEstado = ({
  id_consulta = '',
  enabled = true,
  intervaloMs = 1500,
  desde,
}: {
  id_consulta?: string;
  enabled?: boolean;
  intervaloMs?: number;
  /**
   * Desde qué orden pedir resultados. Es una función y no un número para que
   * leer el valor actual no cambie la clave de la query: si la clave cambiara,
   * React Query trataría cada tramo como otra consulta y perdería el sondeo.
   */
  desde?: () => number;
} = {}) => {
  return useQuery({
    queryKey: ['migracion-olt-consulta', id_consulta],
    queryFn: async () => {
      const d = desde ? desde() : 0;
      return get<IMigracionOltConsulta>(
        `${BASE_URL}migracion-olt/consulta/${id_consulta}?desde=${d}`,
        true,
      );
    },
    enabled: enabled && !!id_consulta,
    staleTime: 0,
    refetchInterval: (query) => {
      const estado = (query.state.data as { data?: IMigracionOltConsulta } | undefined)
        ?.data?.estado;
      return estado === 'EN_CURSO' || estado === 'PAUSADA' ? intervaloMs : false;
    },
  });
};

/** pausar | reanudar | cancelar la consulta. */
export const useMigracionOltConsultaAccion = () => {
  return useMutation({
    mutationFn: async ({
      id_consulta,
      accion,
    }: {
      id_consulta: string;
      accion: 'pausar' | 'reanudar' | 'cancelar';
    }) =>
      post<IMigracionOltConsulta>(
        `${BASE_URL}migracion-olt/consulta/${id_consulta}/${accion}`,
        {},
        true,
      ),
    onError: (error) => handleAxiosError(error),
  });
};

/**
 * Guarda el estado de los pasos que no son trabajo del servidor (paso 3,
 * preparación) para que una recarga tampoco los pierda. Se mezcla sobre lo que
 * ya había, así que cada paso manda solo su clave.
 */
export const useMigracionOltConsultaContexto = () => {
  return useMutation({
    mutationFn: async ({
      id_consulta,
      contexto,
    }: {
      id_consulta: string;
      contexto: IMigracionOltConsultaContexto;
    }) =>
      post<IMigracionOltConsulta>(
        `${BASE_URL}migracion-olt/consulta/${id_consulta}/contexto`,
        contexto,
        true,
      ),
    // Sin onError: que falle el guardado del contexto no debe interrumpir el
    // paso que lo pidió; lo peor que pasa es que una recarga pida un clic más.
  });
};

/**
 * La corrida de migración, manejada por el SERVIDOR.
 *
 * Antes el bucle vivía acá: un for disparaba una petición por cliente y la
 * pantalla era la dueña del proceso. Recargar la perdía. Ahora se manda la
 * lista una vez y el micro procesa la cola; esto solo pregunta cómo va.
 */
export interface IMigracionOltCorridaItem {
  orden: number;
  serie: string;
  /** PENDIENTE | EN_PROCESO | HECHO | OMITIDO */
  estado: string;
  id_ejecucion: string | null;
  mensaje: string | null;
  fecha_inicio: string | null;
  fecha_fin: string | null;
}

export interface IMigracionOltCorrida {
  id_corrida: string;
  ip_olt: string;
  nombre_olt: string | null;
  archivo_excel: string | null;
  /** EN_CURSO | PAUSADA | TERMINADA | CANCELADA */
  estado: string;
  total: number;
  pendientes: number;
  en_proceso: number;
  hechos: number;
  omitidos: number;
  procesados: number;
  /** La serie que se está migrando en este momento. */
  en_curso_serie: string | null;
  mensaje: string | null;
  us_creacion: string | null;
  fecha_creacion: string | null;
  fecha_fin: string | null;
  cola: IMigracionOltCorridaItem[];
  /** Solo en la respuesta de /corrida/activa. */
  hay_corrida?: boolean;
  /** Solo al iniciar: ya había una corrida viva y se devolvió esa. */
  ya_existia?: boolean;
}

export const useMigracionOltCorridaIniciar = () => {
  return useMutation({
    mutationFn: async (body: {
      ip_olt: string;
      nombre_olt: string;
      confirmacion: string;
      archivo_excel?: string | null;
      /** El id con el que ya se agruparon los pasos de consulta. */
      corrida_id?: string;
      items: unknown[];
    }) => post<IMigracionOltCorrida>(`${BASE_URL}migracion-olt/corrida`, body, true),
    onError: (error) => handleAxiosError(error),
  });
};

/**
 * La corrida viva, si hay. Es lo que permite que al recargar la pantalla se
 * reconstruya como si nunca se hubiera salido.
 */
export const useMigracionOltCorridaActiva = ({
  ip_olt = '',
  enabled = true,
}: { ip_olt?: string; enabled?: boolean } = {}) => {
  return useQuery({
    queryKey: ['migracion-olt-corrida-activa', ip_olt],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (ip_olt) qs.set('ip_olt', ip_olt);
      return get<IMigracionOltCorrida>(
        `${BASE_URL}migracion-olt/corrida/activa?${qs.toString()}`,
        true,
      );
    },
    enabled,
    staleTime: 0,
  });
};

/**
 * Cómo va una corrida. Mientras está EN_CURSO se repregunta sola: el avance lo
 * manda el servidor, no el navegador.
 */
export const useMigracionOltCorridaEstado = ({
  id_corrida = '',
  enabled = true,
  intervaloMs = 2500,
}: { id_corrida?: string; enabled?: boolean; intervaloMs?: number } = {}) => {
  return useQuery({
    queryKey: ['migracion-olt-corrida', id_corrida],
    queryFn: async () =>
      get<IMigracionOltCorrida>(`${BASE_URL}migracion-olt/corrida/${id_corrida}`, true),
    enabled: enabled && !!id_corrida,
    staleTime: 0,
    refetchInterval: (query) => {
      const estado = (query.state.data as { data?: IMigracionOltCorrida } | undefined)
        ?.data?.estado;
      // Terminada o cancelada no cambia más: se deja de preguntar.
      return estado === 'EN_CURSO' || estado === 'PAUSADA' ? intervaloMs : false;
    },
  });
};

/** pausar | reanudar | cancelar. */
export const useMigracionOltCorridaAccion = () => {
  return useMutation({
    mutationFn: async ({
      id_corrida,
      accion,
    }: {
      id_corrida: string;
      accion: 'pausar' | 'reanudar' | 'cancelar';
    }) =>
      post<IMigracionOltCorrida>(
        `${BASE_URL}migracion-olt/corrida/${id_corrida}/${accion}`,
        {},
        true,
      ),
    onError: (error) => handleAxiosError(error),
  });
};

/**
 * Catálogo de service profiles: la fuente EXTRA de datos para aprovisionar.
 *
 * El micro valida el modelo del equipo contra su propio inventario y rechaza la
 * activación si no lo conoce. Esta tabla aporta el nombre que sí acepta cuando
 * el inventario del ERP o el Excel lo traen escrito de otra forma, y el comodín
 * para los modelos que no están.
 */
export interface IServiceProfileCatalogo {
  id_service_profile: string;
  nombre: string;
  id_perfil: number;
  /** El comodín. Solo hay uno y no se puede desactivar. */
  es_generico: boolean;
  activo: boolean;
  observacion: string | null;
  us_creacion: string | null;
  fecha_creacion: string | null;
  us_modificacion: string | null;
  fecha_modificacion: string | null;
}

export const useServiceProfiles = ({ enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery({
    queryKey: ['migracion-olt-service-profiles'],
    queryFn: async () =>
      get<{ total: number; resultados: IServiceProfileCatalogo[] }>(
        `${BASE_URL}migracion-olt/service-profiles`,
        true,
      ),
    enabled,
    staleTime: 60_000,
  });
};

export const useServiceProfileCrear = () => {
  return useMutation({
    mutationFn: async (body: { nombre: string; id_perfil: number; observacion?: string }) =>
      post<IServiceProfileCatalogo>(`${BASE_URL}migracion-olt/service-profiles`, body, true),
    onError: (error) => handleAxiosError(error),
  });
};

export const useServiceProfileActualizar = () => {
  return useMutation({
    mutationFn: async ({
      id_service_profile,
      ...body
    }: {
      id_service_profile: string;
      nombre?: string;
      id_perfil?: number;
      observacion?: string;
      activo?: boolean;
    }) =>
      put<IServiceProfileCatalogo>(
        `${BASE_URL}migracion-olt/service-profiles/${id_service_profile}`,
        body,
        true,
      ),
    onError: (error) => handleAxiosError(error),
  });
};

/** Baja lógica: la fila se conserva para poder auditar migraciones viejas. */
export const useServiceProfileDesactivar = () => {
  return useMutation({
    mutationFn: async (id_service_profile: string) =>
      remove<IServiceProfileCatalogo>(
        `${BASE_URL}migracion-olt/service-profiles/${id_service_profile}`,
        true,
      ),
    onError: (error) => handleAxiosError(error),
  });
};

/**
 * TODAS las ejecuciones de una corrida (o de una OLT).
 *
 * La pantalla de auditoría lista las corridas y, al abrir una, muestra todo lo
 * que pasó en ella: lo migrado, lo rechazado y lo que quedó sin servicio, para
 * poder reintentar desde ahí.
 */
export const useMigracionOltEjecuciones = ({
  corrida_id = '',
  ip_olt = '',
  limite = 200,
  enabled = true,
}: { corrida_id?: string; ip_olt?: string; limite?: number; enabled?: boolean } = {}) => {
  return useQuery({
    queryKey: ['migracion-olt-ejecuciones', corrida_id, ip_olt, limite],
    queryFn: async () => {
      const qs = new URLSearchParams({ limite: String(limite) });
      if (corrida_id) qs.set('corrida_id', corrida_id);
      if (ip_olt) qs.set('ip_olt', ip_olt);
      return get<{
        corrida_id: string | null;
        devueltas: number;
        completados: number;
        acs_pendiente: number;
        sin_servicio: number;
        rechazados: number;
        resultados: IMigracionOltResultadoMigracion[];
      }>(`${BASE_URL}migracion-olt/ejecuciones?${qs.toString()}`, true);
    },
    enabled: enabled && (!!corrida_id || !!ip_olt),
    staleTime: 10_000,
  });
};
